import { ReportChapterObject } from '../../base';
// Remove createScores if only using CaseStudies
// import { createScores } from '../../base/scoreHelper';
// Import CaseStudies namespace
import * as CaseStudies from '../../content/case-study';

export const benchmarks: Omit<ReportChapterObject, 'number'> = {
    id: 'benchmarks',
    caption: `Benchmarks`,
    title: `Measurable Benchmarks Demonstrate Clear Value of GenAI in Software Development`,
    description: `Organizations leveraging generative AI (GenAI) in software development measure success through clearly defined benchmarks related to productivity, quality, operational efficiency, and adoption. These benchmarks provide actionable insights and quantitative evidence supporting broader adoption and investment in GenAI technologies.`,
    editedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
    sections: [
        {
            id: '1',
            title: `Key Benchmarks Demonstrating GenAI Value`,
            caption: `Benchmark Metrics`,
            content: [
                {
                    kind: 'text',
                    text: `Enterprises track key performance indicators that highlight the benefits of adopting generative AI tools. These include measurable increases in developer productivity, significant reductions in maintenance efforts, improvements in code quality and test coverage, faster task resolution times, high acceptance rates of AI-generated code, and notable operational efficiencies.`,
                },
                {
                    kind: 'table',
                    id: 'genai-software-dev-metrics',
                    title: 'GenAI Software Development Benchmarks',
                    description:
                        'Aggregated benchmarks and improvements observed across various enterprise implementations of GenAI tools.',
                    caption: null,
                    columns: [
                        {
                            key: 'category',
                            title: 'Category',
                            description: '',
                            type: 'string',
                        },
                        {
                            key: 'metric',
                            title: 'Metric(s)',
                            description: '',
                            type: 'string',
                        },
                        {
                            key: 'improvement',
                            title: 'Improvement with GenAI',
                            description: '',
                            type: 'string',
                        },
                        {
                            key: 'details',
                            title: 'Details',
                            description: '',
                            type: 'string',
                        },
                    ],
                    rows: [
                        {
                            id: '1',
                            data: {
                                category: 'Developer Productivity',
                                metric: 'Coding speed, delivery time',
                                improvement:
                                    '20-40% increase in productivity - 20-30% faster code delivery - 20-40% fewer hours on repetitive tasks',
                                details:
                                    'Up to 90-99% time savings for highly repetitive scripting tasks, though these represent optimal scenarios rather than typical use cases',
                            },
                        },
                        {
                            id: '2',
                            data: {
                                category: 'Engineering Effort',
                                metric: 'Time spent on maintenance vs. innovation',
                                improvement:
                                    '20-40% reduction in maintenance time - Corresponding increase in time for new feature development',
                                details:
                                    'Shift noted at Cisco: "Instead of spending 30 hours coding, I spend 20-25 hours coding and more time on designing architecture"',
                            },
                        },
                        {
                            id: '3',
                            data: {
                                category: 'Time-to-Resolution',
                                metric: 'Task completion time',
                                improvement: `Hours/days reduced to minutes for onboarding, setup, support tasks - 30-50% overall workflow time reduction`,
                                details: `Most significant gains seen in specific activities like log onboarding, threat modeling, and SQL query drafting`,
                            },
                        },
                        {
                            id: '4',
                            data: {
                                category: 'Code Quality & Coverage',
                                metric: 'Test coverage, defect rates',
                                improvement: `Legacy projects improved from 3% to 50% coverage - Some files reaching 100% coverage - Reduction in production defects`,
                                details: `Coverage improvements most dramatic in legacy projects with initially poor testing; results vary by codebase complexity`,
                            },
                        },
                        {
                            id: '5',
                            data: {
                                category: 'Adoption & Acceptance',
                                metric: 'AI-generated code acceptance rate, User satisfaction',
                                improvement: `25-33% of AI-suggested code accepted and merged - 75-93% of developers report equal or better productivity`,
                                details: `Acceptance rates vary significantly by task type and technology area`,
                            },
                        },
                        {
                            id: '6',
                            data: {
                                category: 'Operational Impact',
                                metric: 'Engineering hours saved, AI usage volume',
                                improvement: `13,000 hours saved by automating on-call support - Tens of thousands of questions answered daily - Hundreds of thousands of lines of code generated daily`,
                                details: `Benefits scale with organization size; most dramatic results in enterprises with 50,000+ employees`,
                            },
                        },
                    ],
                },
                {
                    kind: 'text',
                    text: `Benchmarks were derived by aggregating quantitative productivity gains, standardizing the data into consistent ranges, and evaluating specific before-and-after scenarios for common development workflows. Time reallocations between maintenance and feature development were quantified based on direct input from engineering teams.`,
                },
                {
                    kind: 'text',
                    text: `User feedback and adoption metrics were consolidated to determine realistic acceptance rates, ensuring the data reflects typical scenarios rather than outliers. Exceptional cases with extremely high or low outcomes were identified but excluded from overall benchmark ranges to maintain accuracy and reliability.`,
                },
            ],
            keywords: [],
            sources: [
                CaseStudies.ciscoCaseStudy,
                CaseStudies.mondayCaseStudy,
                CaseStudies.goldmanSachsCaseStudy,
                CaseStudies.tdBankCaseStudy,
                CaseStudies.airbnbDevPlatformCaseStudy,
                CaseStudies.transunionCaseStudy,
                CaseStudies.santanderCaseStudy,
                CaseStudies.uberQueryGPTCaseStudy,
                CaseStudies.uberAutocoverCaseStudy,
                CaseStudies.capgeminiCaseStudy,
                CaseStudies.attCaseStudy,
                CaseStudies.uberGenieCaseStudy,
                CaseStudies.jpmorganCaseStudy,
                CaseStudies.citiBankCaseStudy,
                CaseStudies.sunlifeCaseStudy,
                CaseStudies.slackCaseStudy,
            ],
        },
    ],
};
